import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, addMonths, subMonths } from 'date-fns';
import axios from 'axios';
import { FHOST } from '../constants/Functions';

const Scheduler = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [availableTimes, setAvailableTimes] = useState({});
  const [filteredTimes, setFilteredTimes] = useState([]);
  const [timeRange, setTimeRange] = useState({ start: "", end: "" });

  const teacherId = 9;

  // Days of the week
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Generate days for the current month
  const generateDays = () => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    const days = eachDayOfInterval({ start, end });

    // Include padding for the start of the month
    const startPadding = startOfWeek(start);
    while (startPadding < start) {
      days.unshift(new Date(startPadding));
      startPadding.setDate(startPadding.getDate() + 1);
    }
    return days;
  };

  // Fetch available times from the backend
  const fetchAvailableTimes = async () => {
    try {
      const response = await axios.get(`${FHOST}/lessons/api/get-available-times/${teacherId}`);
      if (response.status === 200) {
        const data = response.data; // Assuming the response contains an array of time slots
        const timesByDate = {};

        // Organize the available times by date
        data.forEach((timeSlot) => {
          const { date, start_time, end_time } = timeSlot;
          if (!timesByDate[date]) {
            timesByDate[date] = [];
          }
          timesByDate[date].push({ start_time, end_time });
        });

        setAvailableTimes(timesByDate);
        setFilteredTimes(Object.values(timesByDate).flat()); // Set initial filtered data to all available times
      }
    } catch (error) {
      console.error("Error fetching available times:", error);
      alert("Failed to load available times. Please try again.");
    }
  };

  // Call fetchAvailableTimes when the component mounts
  useEffect(() => {
    fetchAvailableTimes();
  }, []);

  const handleDayClick = (day) => {
    setSelectedDay(day);

    // Filter available times based on the selected day
    const dateKey = format(day, "yyyy-MM-dd");
    const filtered = availableTimes[dateKey] || []; // Get times for that specific date
    setFilteredTimes(filtered);
  };

  const addTimeRange = async () => {
    const { start, end } = timeRange;

    if (!start || !end) return alert("Please select both start and end times.");
    if (start >= end) return alert("Start time must be earlier than end time.");

    const dateKey = format(selectedDay, "yyyy-MM-dd");

    // Combine date with start and end times in normal timestamp format
    const startTimestamp = `${dateKey} ${start}:00`;
    const endTimestamp = `${dateKey} ${end}:00`;

    // Prepare payload
    const payload = {
      date: dateKey,
      start: startTimestamp,
      end: endTimestamp,
    };

    try {
      // Send to backend using axios
      const response = await axios.post(`${FHOST}/lessons/api/submit-time-range/${teacherId}`, payload);

      if (response.status === 200) {
        alert(response.data.message || "Time range submitted successfully!");

        // Update local state to reflect the added time range
        const updatedAvailableTimes = { ...availableTimes };
        if (!updatedAvailableTimes[dateKey]) {
          updatedAvailableTimes[dateKey] = [];
        }
        updatedAvailableTimes[dateKey].push({ start, end });

        setAvailableTimes(updatedAvailableTimes);

        // Filter the available times again for the selected day
        const filtered = updatedAvailableTimes[dateKey] || [];
        setFilteredTimes(filtered);

        setTimeRange({ start: "", end: "" }); // Clear input
      } else {
        alert("Failed to submit time range. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting time range:", error);
      alert("An error occurred. Please try again.");
    }
  };

  const changeMonth = (direction) => {
    setCurrentDate(direction === "prev" ? subMonths(currentDate, 1) : addMonths(currentDate, 1));
    setSelectedDay(null); // Deselect any selected day
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-center mb-4">Scheduler</h1>
      {/* Calendar Header */}
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => changeMonth("prev")}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Prev
        </button>
        <h2 className="text-lg font-semibold">{format(currentDate, "MMMM yyyy")}</h2>
        <button
          onClick={() => changeMonth("next")}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Next
        </button>
      </div>

      {/* Days of the Week */}
      <div className="grid grid-cols-7 gap-2 text-center text-gray-700 mb-2">
        {daysOfWeek.map((day) => (
          <div key={day} className="font-semibold">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">
        {generateDays().map((day, index) => (
          <button
            key={index}
            onClick={() => handleDayClick(day)}
            className={`p-4 rounded text-sm font-medium ${
              selectedDay && format(day, "yyyy-MM-dd") === format(selectedDay, "yyyy-MM-dd")
                ? "bg-blue-500 text-white"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
          >
            {format(day, "d")}
          </button>
        ))}
      </div>

      {/* Selected Day & Time Picker */}
      {selectedDay && (
        <div className="mt-6 p-4 bg-gray-100 rounded shadow-md">
          <h3 className="text-lg font-semibold mb-4">
            Selected Day: {format(selectedDay, "EEEE, MMM d")}
          </h3>
          <div className="flex gap-4 items-center mb-4">
            <div>
              <label htmlFor="start-time" className="block font-medium mb-1">
                Start Time:
              </label>
              <input
                id="start-time"
                type="time"
                value={timeRange.start}
                onChange={(e) => setTimeRange({ ...timeRange, start: e.target.value })}
                className="border border-gray-300 p-2 rounded"
              />
            </div>
            <div>
              <label htmlFor="end-time" className="block font-medium mb-1">
                End Time:
              </label>
              <input
                id="end-time"
                type="time"
                value={timeRange.end}
                onChange={(e) => setTimeRange({ ...timeRange, end: e.target.value })}
                className="border border-gray-300 p-2 rounded"
              />
            </div>
            <button
              onClick={addTimeRange}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
              Add Time Range
            </button>
          </div>

          {/* Display Available Times */}
          <div>
            <h4 className="font-semibold mb-2">Available Time Ranges:</h4>
            <ul className="list-disc pl-5">
              {filteredTimes.map((time, idx) => (
                <li key={idx}>
                  {time.start_time} - {time.end_time}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default Scheduler;
